<template>
  <LoadingComponent v-if="loading" />
  <template v-else-if="!loading && localDashboard">
    <div class="shrink-0">
      <DashboardHeader :dashboard="localDashboard">
        <template #cta>
          <DashboardPanelControl
            v-model:dashboard="localDashboard"
            v-model:require-save-dashboard="requireSaveDashboard"
            :need-save-locked="loading"
            :count-filter="validFilter.length ?? 0"
            :is-dirty="$isStateDirty('localDashboard.filters')"
            @dashboard-changed="dashboardChanged"
            @clear-filters="clearFilters"
          >
            <template #display-options-content="{ close }">
              <DisplayOptionsContent
                :sorting="localDashboard.sort"
                @update:sorting="updateSorting"
                @close-popover="() => closePopover(close)"
              />
            </template>
            <template #other-cta="{ close }">
              <div class="flex gap-2">
                <BaseButton
                  :title="$t('button.add')"
                  icon="plus"
                  color="primary"
                  size="small"
                  :disabled="loadingEvent"
                  @click="handleEventClick"
                />
                <BaseButton
                  :title="$t('button.print')"
                  icon="print"
                  color="secondary"
                  size="small"
                  class="print:hidden"
                  @click="handlePrint"
                />
              </div>
            </template>
            <template #filter-content="{ close }">
              <FilterLinesComponent
                v-model:filters="localDashboard.filters"
                v-model:require-save-dashboard="requireSaveDashboard"
                :filters-available="availableFilter"
                @close-popover="() => closePopover(close)"
              />
            </template>
          </DashboardPanelControl>
        </template>
        <template #subtitle>
          <div class="flex items-center gap-4">
            <div class="shrink-0">
              <DateRangeNavigator
                v-model:start="specific_day_start"
                v-model:end="specific_day_end"
                size="small"
                @dates-changed="rangeNavigatorChanged"
              />
            </div>
            <div class="text-lg font-bold capitalize">
              {{ periodTitle }}
            </div>
          </div>
        </template>
      </DashboardHeader>
    </div>
    <div class="min-h-0 flex-1 print:h-auto print:min-h-full">
      <EventsContainer
        v-model:group-events="groupedEvents"
        :sort="localDashboard.sort"
        :assignable-people="assignablePeople"
        :assignable-material-resources="assignableMaterialResources"
        :has-funeral-home-filter="hasFuneralHomeFilter"
        :all-users-availability-items="allUsersAvailabilityItems"
        :all-material-resources-availability-items="
          allMaterialResourcesAvailabilityItems
        "
        @fetch-events="fetchEvents()"
      />
    </div>
  </template>
  <EventModal
    v-model:open="isEventModalOpen"
    :allow_events="[]"
    :full-day-event="false"
    :new-event-from="newEventFrom"
    :new-event-to="newEventTo"
    :new-event-place="null"
    :new-event-place-type="null"
    :new-event-place-kind="null"
    :is-in-planning="true"
    :force-form="true"
    @close="isEventModalOpen = false"
    @event-updated="fetchEvents"
  />
</template>

<script>
import LoadingComponent from '@c/TheLoadingComponent.vue'
import DashboardPanelControl from '@c/Dashboard/Components/DashboardPanelControl.vue'
import FilterLinesComponent from '@c/Dashboard/Components/FilterLinesComponent.vue'
import DashboardHeader from '@c/Dashboard/Components/DashboardHeader.vue'
import EventsContainer from '@c/Dashboard/Content/Planning/components/EventsContainer.vue'
import { mapActions, mapGetters } from 'vuex'
import dateHelpers from '@u/helpers/dateHelpers'
import DateRangeNavigator from '@c/SelectComponent/DateRangeNavigator.vue'
import DisplayOptionsContent from '@c/Dashboard/Content/Planning/components/DisplayOptionsContent.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import EventModal from '@ui/modal/event/EventModal.vue'
import moment from 'moment'
import persistViewFiltersStateMixin from '@/mixins/persistViewFiltersStateMixin'

export default {
  name: 'PlanningContent',
  components: {
    BaseButton,
    EventModal,
    DisplayOptionsContent,
    DateRangeNavigator,
    EventsContainer,
    LoadingComponent,
    DashboardPanelControl,
    FilterLinesComponent,
    DashboardHeader
  },
  mixins: [persistViewFiltersStateMixin],
  persistViewState: [
    'specific_day_enabled',
    'specific_day_start',
    'specific_day_end',
    'localDashboard.filters'
  ],
  props: {
    currentBoard: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      localDashboard: null,
      events: [],
      specific_day_enabled: false,
      specific_day_start: null,
      specific_day_end: null,
      loading: false,
      loadingEvent: false,
      groupedEvents: [],
      requireSaveDashboard: false,
      isEventModalOpen: false,
      newEventFrom: null,
      newEventTo: null,
      allUsersAvailabilityItems: [],
      allMaterialResourcesAvailabilityItems: []
    }
  },
  computed: {
    ...mapGetters({
      availableFilter: 'metadata/getAvailableFilterPlanning',
      users: 'metadata/getUsersMetadata',
      meterial_ressources: 'metadata/getMaterialResourcesMetadata'
    }),
    hasFuneralHomeFilter() {
      return this.currentBoard?.filters
        ?.map((filter) => filter.column)
        ?.includes('funeral_home_id')
    },
    validFilter() {
      return (
        this.localDashboard.filters?.filter(
          (filter) => filter.column && filter.values && filter.condition
        ) ?? []
      )
    },
    assignablePeople() {
      return this.users.map((user) => {
        return {
          user: user,
          events: this.events
            .filter((event) => event.assignedPeople.includes(user.id))
            .map((event) => {
              return {
                title: event.default_title ?? event.title,
                date_from: event.date_from,
                date_to: event.date_to,
                assignement_status: event.assignedPeopleCollection.find(
                  (person) => person.id === user.id
                )?.disponibility?.assignement_status
              }
            })
        }
      })
    },
    assignableMaterialResources() {
      return this.meterial_ressources.map((material_resource) => {
        return {
          material_resource: material_resource,
          events: this.events
            .filter((event) =>
              event.materialResources.includes(material_resource.id)
            )
            .map((event) => {
              return {
                title: event.title,
                date_from: event.date_from,
                date_to: event.date_to,
                assignement_status: event.materialResourcesCollection.find(
                  (assigned_material_resouce) =>
                    assigned_material_resouce.id === material_resource.id
                )?.disponibility?.assignement_status
              }
            })
        }
      })
    },
    periodTitle() {
      if (
        this.specific_day_enabled &&
        this.specific_day_start &&
        this.specific_day_end
      ) {
        return this.$h.getDateRangeString(
          this.specific_day_start,
          this.specific_day_end,
          'ddd ll'
        )
      }
      let filters =
        this.localDashboard.filters.filter((filter) =>
          ['date_range', 'date_specific', 'date_relative'].includes(
            filter.column
          )
        ) ?? []
      if (filters.length && filters[0].values) {
        if (filters[0].column === 'date_range') {
          return this.$h.getDateRangeString(
            filters[0].values.start,
            filters[0].values.end,
            'ddd ll'
          )
        } else if (filters[0].column === 'date_relative') {
          return `${this.$t(
            `enums.common.relativeDates.${filters[0].values}`
          )} - ${this.$h.getRelativeDateString(filters[0].values, 'ddd ll')}`
        } else if (filters[0].column === 'date_specific') {
          return this.$h.getDateRangeString(
            filters[0].values,
            filters[0].values,
            'ddd ll'
          )
        }
      }
      return '-'
    }
  },
  watch: {
    currentBoard(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.loading = true
        this.localDashboard = newValue
        this.fetchPlanningDashboardConfig()
      }
    },
    events: {
      handler(newValue) {
        // this.groupAndSortEvents();
      },
      deep: true
    },
    'localDashboard.filters': {
      handler(newValue) {
        this.filterUpdated()
      },
      deep: true
    }
  },
  mounted() {
    this.fetchPlanningDashboardConfig()
  },
  methods: {
    ...mapActions({
      processFetchTemplateDashboardByIdAction:
        'dashboard/fetchTemplateDashboardById',
      processFetchEventsAction: 'planning/fetchEvents'
    }),
    clearFilters() {
      this.localDashboard.filters = this.$getInitialState(
        'localDashboard.filters'
      )
      this.fetchEvents()
    },
    fetchEvents: function () {
      this.events = []
      this.loadingEvent = true
      let params = {
        filters: this.validFilter
      }
      if (
        this.specific_day_enabled &&
        this.specific_day_start &&
        this.specific_day_end
      ) {
        params = {
          ...params,
          start: this.specific_day_start,
          end: this.specific_day_end
        }
      }
      this.processFetchEventsAction(params).then((response) => {
        this.events = response.events
        this.allUsersAvailabilityItems = response.users_availability_items
        this.allMaterialResourcesAvailabilityItems =
          response.material_resources_availability_items
        this.loadingEvent = false
        this.sortedGroupedEvents()
      })
    },
    fetchPlanningDashboardConfig() {
      this.loading = true
      const targetId = this.currentBoard.id
      this.processFetchTemplateDashboardByIdAction({ id: targetId })
        .then((response) => {
          if (this.currentBoard.id !== targetId) return
          this.localDashboard = response.template_dashboard
          this.$restoreCachedState('localDashboard.filters', response.template_dashboard.filters)
          if (
            !this.localDashboard.filters.some((filter) =>
              ['date_relative', 'date_specific', 'date_range'].includes(
                filter.column
              )
            )
          ) {
            this.localDashboard.filters.push({
              whereType: 'where',
              column: 'date_relative',
              values: 'today',
              condition: '='
            })
          }
          this.fetchEvents()
        })
        .catch((error) => {
          this.$router.push({ name: 'dashboard_main_page_route' })
        })
        .finally((_) => {
          if (this.currentBoard.id === targetId) this.loading = false
        })
    },
    closePopover(close) {
      if (typeof close === 'function') {
        close()
      }
      this.fetchEvents(this.currentStartDate, this.currentEndDate)
    },
    handleEventClick({ event }) {
      this.fullDayEvent = false
      this.newEventFrom = moment(this.specific_day_end + ' 08:00').format()
      this.newEventTo = moment(this.specific_day_end + ' 08:00').add(1, 'hour')
      this.isEventModalOpen = true
    },
    handlePrint() {
      window.print()
    },
    dashboardChanged(_dashboard_template_id) {
      this.fetchPlanningDashboardConfig()
    },
    updateSorting(newSorting) {
      this.localDashboard.sort = newSorting

      this.sortedGroupedEvents()
    },
    sortedGroupedEvents() {
      const direction = this.localDashboard.sort?.direction || 'asc'
      const isAsc = direction === 'asc'
      let sortedEvents = [...this.events]

      if (this.localDashboard.sort && this.localDashboard.sort.group) {
        const groupKey = this.localDashboard.sort.group

        if (groupKey === 'date') {
          sortedEvents.sort((a, b) =>
            isAsc
              ? new Date(a.date_from) - new Date(b.date_from)
              : new Date(b.date_from) - new Date(a.date_from)
          )
        } else if (groupKey === 'deceased') {
          sortedEvents.sort((a, b) =>
            isAsc
              ? a.dossier?.concernedPerson?.full_name -
                b.dossier?.concernedPerson?.full_name
              : b.dossier?.concernedPerson?.full_name -
                a.dossier?.concernedPerson?.full_name
          )
        }
      } else {
        sortedEvents.sort((a, b) =>
          isAsc
            ? new Date(a.date_from) - new Date(b.date_from)
            : new Date(b.date_from) - new Date(a.date_from)
        )
      }

      this.groupedEvents = this.groupBy(
        sortedEvents,
        this.localDashboard.sort?.group,
        direction
      )
    },
    groupBy(events, key, direction = 'asc') {
      const groupedEvents = events.reduce((result, event) => {
        let value = ''

        if (key === 'date') {
          value = dateHelpers.momentLocalised(
            event.date_from,
            'dddd D MMMM YYYY'
          )
        } else if (key === 'deceased') {
          value = event.dossier?.concernedPerson?.full_name || ''
        }

        if (!result[value]) {
          result[value] = []
        }
        result[value].push(event)

        return result
      }, {})

      const sortedKeys = Object.keys(groupedEvents).sort((a, b) => {
        if (key === 'date') {
          const dateA = new Date(a.split(' ').slice(1).join(' '))
          const dateB = new Date(b.split(' ').slice(1).join(' '))
          return direction === 'asc' ? dateA - dateB : dateB - dateA
        } else if (key === 'deceased') {
          return direction === 'asc' ? a.localeCompare(b) : b.localeCompare(a)
        }
      })

      const sortedGroupedEvents = {}
      sortedKeys.forEach((key) => {
        sortedGroupedEvents[key] = groupedEvents[key]
      })

      return sortedGroupedEvents
    },
    filterUpdated() {
      let filters =
        this.localDashboard.filters.filter((filter) =>
          ['date_range', 'date_specific', 'date_relative'].includes(
            filter.column
          )
        ) ?? []
      if (filters.length && filters[0].values !== null) {
        if (filters[0].column === 'date_range') {
          this.specific_day_start = filters[0].values.start
          this.specific_day_end = filters[0].values.end
        } else if (filters[0].column === 'date_relative') {
          let relativeDates = this.$h.getRelativeDateAsArray(filters[0].values)
          if (Array.isArray(relativeDates) && relativeDates.length === 2) {
            this.specific_day_start = relativeDates[0].format('YYYY-MM-DD')
            this.specific_day_end = relativeDates[1].format('YYYY-MM-DD')
          }
        } else if (filters[0].column === 'date_specific') {
          this.specific_day_start = filters[0].values
          this.specific_day_end = filters[0].values
        }
      }
    },
    rangeNavigatorChanged() {
      this.specific_day_enabled = true
      this.fetchEvents()
    }
  }
}
</script>
